import React from "react";
import { useUiPreferences } from "./useUiPreferences";

/**
 * Option einer Zahlungsart-Auswahl. Die Masken liefern den Wert mal als Zahl, mal — wie bei antd
 * üblich — als Zeichenkette; verglichen wird deshalb numerisch.
 */
type PaymentMethodOptionLike = { value?: unknown };

function toMethodNumber(value: unknown): number | null {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return num;
}

/**
 * Zuletzt bei einer **manuellen Zahlungserfassung** gewählte Zahlungsart — eine persönliche
 * Arbeitspräferenz, kein fachlicher Wert am Datensatz.
 *
 * Sie gilt gemeinsam für alle Masken, in denen ein Mitarbeiter eine tatsächlich geflossene Zahlung
 * erfasst (Zahlung buchen, Sammelzahlung, Artikelverkauf, Wertkonto aufladen): Wer an der Theke bar
 * kassiert, tut das durchgehend, nicht je Maske verschieden. Sie gilt **nicht** für die vereinbarte
 * Zahlart an Vertrag, Beitrag oder offenem Posten und nicht dort, wo die Zahlungsart fachlich
 * vorgegeben ist (z. B. Meldung an einen Inkassofall).
 *
 * Gespeichert wird erst nach einer erfolgreich erfassten Zahlung, nicht bei jeder Auswahl im
 * Formular — ein abgebrochener Vorgang soll die Vorbelegung nicht verändern.
 */
export function useLastPaymentMethod() {
  const { prefs, patchPrefs } = useUiPreferences();
  const lastPaymentMethod = toMethodNumber(prefs.paymentEntry?.lastMethod);

  const rememberPaymentMethod = React.useCallback(
    (method: unknown) => {
      const next = toMethodNumber(method);
      if (next == null || next === lastPaymentMethod) return;
      patchPrefs({ paymentEntry: { lastMethod: next } });
    },
    [lastPaymentMethod, patchPrefs]
  );

  /**
   * Vorbelegung einer Zahlungsart-Auswahl: die gemerkte Zahlungsart, sofern sie beim Mandanten
   * noch angeboten wird — sonst `fallback`. Ohne `options` (Liste noch nicht geladen) wird nicht
   * vorbelegt, damit keine inzwischen deaktivierte Zahlungsart gesetzt wird.
   */
  const resolveDefaultPaymentMethod = React.useCallback(
    (options: ReadonlyArray<PaymentMethodOptionLike> | null | undefined, fallback: number | null) => {
      if (lastPaymentMethod == null || !options?.length) return fallback;
      const isOffered = options.some((opt) => toMethodNumber(opt?.value) === lastPaymentMethod);
      return isOffered ? lastPaymentMethod : fallback;
    },
    [lastPaymentMethod]
  );

  return { lastPaymentMethod, rememberPaymentMethod, resolveDefaultPaymentMethod };
}
